import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { supabase } from "../../supaBaseClient";
import { API_BASE } from "../../config";

const Progress = () => {
  // State
  const [userLevel, setUserLevel] = useState("");
  const [completedTopics, setCompletedTopics] = useState(0);
  const [totalTopics, setTotalTopics] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [recommendations, setRecommendations] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [loadingAI, setLoadingAI] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  // Auth: get current user and listen for changes
  useEffect(() => {
    let unsub = () => {};

    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!error && data?.user) setUserId(data.user.id);
      else setUserId(null);

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        setUserId(session?.user?.id ?? null);
      });

      unsub = () => subscription.unsubscribe();
    })();

    return () => unsub();
  }, []);

  // Fetch progress + AI recommendations
  useEffect(() => {
    if (!userId) {
      setLoadingProgress(false);
      setLoadingAI(false);
      return;
    }

    const fetchUserProgress = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/progress/progress/${userId}`);
        setUserLevel(res.data.userLevel);
        setCompletedTopics(res.data.completedTopics);
        setTotalTopics(res.data.totalTopics);
        setProgressPercentage(res.data.completionPercentage);
      } catch (err) {
        console.error("Error fetching progress data:", err);
        setError("Failed to load progress data.");
      } finally {
        setLoadingProgress(false);
      }
    };

    const fetchAIRecommendations = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/progress/completed/${userId}`);
        setRecommendations(res.data.recommendations || "No recommendations available.");
      } catch (err) {
        console.error("Error fetching AI recommendations:", err);
        setRecommendations("Failed to load AI recommendations.");
      } finally {
        setLoadingAI(false);
      }
    };

    setLoadingProgress(true);
    setLoadingAI(true);
    setError(null);
    fetchUserProgress();
    fetchAIRecommendations();
  }, [userId]);

  if (loadingProgress) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600 font-semibold">Loading progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-center text-red-600 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <p className="text-gray-600 font-semibold">Please sign in to view your progress.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Helmet>
        <title>Progress | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/progress" />
        <meta name="description" content="Track your learning progress, view AI-powered recommendations, and see your completed topics on MLMaths." />
      </Helmet>

      <main className="container mx-auto px-6 py-10 max-w-5xl">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Your Learning Progress</h1>
          <p className="text-lg text-gray-600">
            Current Level: <span className="font-bold text-teal-600">{userLevel}</span>
          </p>
        </div>

        {/* Main Progress Card - Single unified display */}
        <div className="bg-white shadow-xl rounded-2xl p-8 mb-8 border-t-4 border-teal-500">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Overall Progress</h2>
          
          {/* Large Progress Display */}
          <div className="text-center mb-8">
            <div className="inline-block">
              <div className="text-6xl font-bold text-teal-600 mb-2">
                {completedTopics} <span className="text-3xl text-gray-400">/ {totalTopics}</span>
              </div>
              <p className="text-gray-600 text-lg">Topics Completed</p>
            </div>
          </div>

          {/* Progress Bar - Only shows color if > 0% */}
          <div className="relative mb-6">
            <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden shadow-inner">
              {progressPercentage > 0 ? (
                <div
                  className="bg-gradient-to-r from-teal-500 to-teal-600 h-8 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                  style={{ width: `${progressPercentage}%` }}
                >
                  {progressPercentage > 10 && (
                    <span className="text-white font-bold text-sm">{progressPercentage}%</span>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-8">
                  <span className="text-gray-500 text-sm">0% Complete - Start your journey!</span>
                </div>
              )}
            </div>
          </div>

          {/* Percentage Display Below Bar */}
          <p className="text-center text-2xl font-bold text-gray-700">
            {progressPercentage}% Complete
          </p>
        </div>

        {/* Two Column Layout - Different Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Topic Breakdown Card */}
          <div className="bg-white shadow-xl rounded-2xl p-8 border-l-4 border-blue-500">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Topic Breakdown</h2>
                <p className="text-gray-600">Your learning journey</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-green-50 rounded-xl p-4 flex justify-between items-center">
                <span className="text-gray-700 font-medium">Completed</span>
                <span className="text-3xl font-bold text-green-600">{completedTopics}</span>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 flex justify-between items-center">
                <span className="text-gray-700 font-medium">In Progress</span>
                <span className="text-3xl font-bold text-orange-600">
                  {totalTopics - completedTopics}
                </span>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 flex justify-between items-center">
                <span className="text-gray-700 font-medium">Total Topics</span>
                <span className="text-3xl font-bold text-blue-600">{totalTopics}</span>
              </div>
            </div>
          </div>

          {/* Motivational Card */}
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 shadow-xl rounded-2xl p-8 text-white">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-white bg-opacity-20 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {progressPercentage === 0 ? "Get Started!" : 
                   progressPercentage < 25 ? "Keep Going!" :
                   progressPercentage < 50 ? "Great Progress!" :
                   progressPercentage < 75 ? "Almost There!" :
                   progressPercentage < 100 ? "Nearly Done!" :
                   "Congratulations!"}
                </h2>
                <p className="text-teal-100">
                  {progressPercentage === 0 ? "Begin your GCSE Maths journey" :
                   progressPercentage < 100 ? "You're making excellent progress" :
                   "You've completed all topics!"}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-sm text-teal-100 mb-1">Current Level</p>
                <p className="text-2xl font-bold">{userLevel}</p>
              </div>
              {progressPercentage > 0 && (
                <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                  <p className="text-sm text-teal-100 mb-1">Topics Mastered</p>
                  <p className="text-2xl font-bold">{completedTopics} topics</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AI Recommendations Card */}
        <div className="bg-white shadow-xl rounded-2xl p-8 border-l-4 border-purple-500">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-purple-100 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">AI Recommendations</h2>
              <p className="text-gray-600">Personalised suggestions for your learning journey</p>
            </div>
          </div>

          {loadingAI ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-teal-600"></div>
              <span className="ml-3 text-gray-600">Generating recommendations...</span>
            </div>
          ) : (
            <div className="bg-purple-50 rounded-xl p-6">
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown>{recommendations}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Progress;